<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

const props = defineProps({
  error: { type: String, required: true },
  isWhatsapp: { type: Boolean, default: false },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt, content, attachments } =
  useMessageContext();

const { t } = useI18n();

const isUndeliverable = computed(
  () => props.isWhatsapp && /^\s*131026\s*:/.test(props.error)
);
// Meta answers an unsupported audio/video format in English, naming mime types the operator
// never chose; the send already converts what it can, so the rest becomes a human message.
const isUnsupportedMedia = computed(
  () => props.isWhatsapp && /^\s*131053\s*:/.test(props.error)
);
// Conta WhatsApp Business com pagamento pendente na Meta: depois de regularizar, o operador
// precisa reenviar, inclusive modelos enviados há mais de um dia.
const isPaymentIssue = computed(
  () => props.isWhatsapp && /^\s*131042\s*:/.test(props.error)
);
const errorLabel = computed(() => {
  if (isUndeliverable.value) return t('CHAT_LIST.WHATSAPP_UNDELIVERABLE_LABEL');
  if (isPaymentIssue.value) return t('CHAT_LIST.WHATSAPP_PAYMENT_ISSUE_LABEL');
  if (isUnsupportedMedia.value)
    return t('CHAT_LIST.WHATSAPP_UNSUPPORTED_MEDIA_LABEL');
  return t('CHAT_LIST.FAILED_TO_SEND');
});
const errorDescription = computed(() => {
  if (isUndeliverable.value)
    return t('CHAT_LIST.WHATSAPP_UNDELIVERABLE_DESCRIPTION');
  if (isPaymentIssue.value)
    return t('CHAT_LIST.WHATSAPP_PAYMENT_ISSUE_DESCRIPTION');
  if (isUnsupportedMedia.value)
    return t('CHAT_LIST.WHATSAPP_UNSUPPORTED_MEDIA_DESCRIPTION');
  return props.error;
});

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  if (!hasContent && !hasAttachments) return false;
  return isPaymentIssue.value || !hasOneDayPassed(createdAt.value);
});
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ errorLabel }}</span>
    <div class="relative group">
      <button
        type="button"
        :aria-label="errorDescription"
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </button>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-64 max-w-[80vw] text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all break-words z-10"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ errorDescription }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
